import { __ } from '@wordpress/i18n';
import {
	useBlockProps,
	InspectorControls,
	MediaUpload,
	MediaUploadCheck,
} from '@wordpress/block-editor';
import {
	PanelBody,
	SelectControl,
	TextControl,
	ToggleControl,
	RangeControl,
	Button,
	Spinner,
	Placeholder,
	Icon,
	ColorPalette,
} from '@wordpress/components';
import ServerSideRender from '@wordpress/server-side-render';
import { useState, useEffect } from '@wordpress/element';
import './editor.scss';

/**
 * Skeleton placeholder shown while ServerSideRender is loading.
 *
 * @return {Element} Skeleton component.
 */
function SkeletonLoader() {
	return (
		<div className="bbpc-sf-skeleton">
			<div className="bbpc-sf-skeleton__cover" />
			<div className="bbpc-sf-skeleton__rows">
				{ [ 1, 2, 3 ].map( ( i ) => (
					<div className="bbpc-sf-skeleton__row" key={ i }>
						<div className="bbpc-sf-skeleton__line bbpc-sf-skeleton__line--title" />
						<div className="bbpc-sf-skeleton__line bbpc-sf-skeleton__line--meta" />
					</div>
				) ) }
			</div>
		</div>
	);
}

export default function Edit( { attributes, setAttributes } ) {
	const blockProps = useBlockProps();

	/* Check if Forumax Pro is active. */
	const isProActive =
		window.bbpc_upsell_config?.is_pro_active === '1' ||
		window.bbpc_upsell_config?.is_pro_active === true;
	const {
		forum_id,
		style,
		cover_image,
		ppp,
		order,
		word_length,
		read_more,
		columns,
		show_voice,
		show_replies,
		show_freshness,
		show_excerpt,
		show_view_all,
		show_cover_badges,
		title_color,
		title_hover_color,
		excerpt_color,
		meta_color,
		bg_color,
		cover_overlay_color,
		border_radius,
		title_font_size,
		meta_font_size,
		title_font_weight,
		row_padding,
		row_gap,
		cover_height,
		hide_voice_mobile,
		hide_replies_mobile,
		hide_user_mobile,
	} = attributes;

	const [ forums, setForums ] = useState( [] );
	const [ isLoading, setIsLoading ] = useState( true );

	// Fetch forums for the select control.
	useEffect( () => {
		setIsLoading( true );

		let ajaxUrl = '/wp-admin/admin-ajax.php';

		if ( typeof window.ajaxurl !== 'undefined' ) {
			ajaxUrl = window.ajaxurl;
		} else if (
			typeof window.bbpc_editor_config !== 'undefined' &&
			window.bbpc_editor_config.ajaxurl
		) {
			ajaxUrl = window.bbpc_editor_config.ajaxurl;
		}

		const formData = new FormData();
		formData.append( 'action', 'bbpc_get_forums' );

		if (
			typeof window.bbpc_editor_config !== 'undefined' &&
			window.bbpc_editor_config.nonce
		) {
			formData.append( 'nonce', window.bbpc_editor_config.nonce );
		}

		fetch( ajaxUrl, {
			method: 'POST',
			body: formData,
		} )
			.then( ( response ) => {
				if ( ! response.ok ) {
					throw new Error( 'Failed to fetch forums' );
				}
				return response.json();
			} )
			.then( ( data ) => {
				if ( data.success && Array.isArray( data.data ) ) {
					setForums( data.data );
				} else {
					setForums( [] );
				}
				setIsLoading( false );
			} )
			.catch( () => {
				setForums( [] );
				setIsLoading( false );
			} );
	}, [] );

	const forumOptions =
		forums && forums.length > 0
			? forums.map( ( forum ) => ( {
				label: forum.title || 'Untitled',
				value: forum.id.toString(),
			} ) )
			: [];

	forumOptions.unshift( {
		label: __( 'Select Forum', 'bbp-core' ),
		value: '',
	} );

	return (
		<div { ...blockProps }>
			<InspectorControls>
				{ /* -------- Preset Skins -------- */ }
				<PanelBody title={ __( 'Preset Skins', 'bbp-core' ) }>
					{ isLoading && <Spinner /> }
					<SelectControl
						label={ __( 'Select Forum', 'bbp-core' ) }
						value={ forum_id }
						options={ forumOptions }
						onChange={ ( value ) =>
							setAttributes( { forum_id: value } )
						}
					/>
					<SelectControl
						label={ __( 'Forums Style', 'bbp-core' ) }
						value={ ( ! isProActive && ( style === '3' || style === '4' ) ) ? '1' : style }
						options={ [
							{
								label: __(
									'01 : Single Forum With Topics',
									'bbp-core'
								),
								value: '1',
							},
							{
								label: __( '02 : Single Forum Card', 'bbp-core' ),
								value: '2',
							},
							{
								label: isProActive
									? __( '03 : Compact List (No Thumbnail)', 'bbp-core' )
									: __( '03 : Compact List (Pro)', 'bbp-core' ),
								value: '3',
								disabled: ! isProActive,
							},
							{
								label: isProActive
									? __( '04 : Grid (Forum Info + Topics)', 'bbp-core' )
									: __( '04 : Grid (Pro)', 'bbp-core' ),
								value: '4',
								disabled: ! isProActive,
							},
						] }
						onChange={ ( value ) => {
							if ( ! isProActive && ( value === '3' || value === '4' ) ) {
								return;
							}
							setAttributes( { style: value } );
						} }
					/>
					{ style === '2' && (
						<RangeControl
							label={ __( 'Columns', 'bbp-core' ) }
							value={ columns }
							onChange={ ( value ) =>
								setAttributes( { columns: value } )
							}
							min={ 1 }
							max={ 4 }
							help={ __(
								'Number of columns for the card layout.',
								'bbp-core'
							) }
						/>
					) }
				</PanelBody>

				{ /* -------- Thumbnail -------- */ }
				<PanelBody
					title={ __( 'Thumbnail', 'bbp-core' ) }
					initialOpen={ false }
				>
					<p>{ __( 'Custom Cover Image', 'bbp-core' ) }</p>
					<MediaUploadCheck>
						<MediaUpload
							onSelect={ ( media ) =>
								setAttributes( { cover_image: media } )
							}
							allowedTypes={ [ 'image' ] }
							value={ cover_image ? cover_image.id : '' }
							render={ ( { open } ) => (
								<Button variant="secondary" onClick={ open }>
									{ cover_image
										? __( 'Replace Image', 'bbp-core' )
										: __( 'Select Image', 'bbp-core' ) }
								</Button>
							) }
						/>
					</MediaUploadCheck>
					{ cover_image && (
						<div style={ { marginTop: '10px' } }>
							<img
								src={ cover_image.url }
								alt="Cover"
								style={ { maxWidth: '100%' } }
							/>
							<Button
								isLink
								isDestructive
								onClick={ () =>
									setAttributes( { cover_image: null } )
								}
							>
								{ __( 'Remove Image', 'bbp-core' ) }
							</Button>
						</div>
					) }
				</PanelBody>

				{ /* -------- Filter Options -------- */ }
				<PanelBody
					title={ __( 'Filter Options', 'bbp-core' ) }
					initialOpen={ false }
				>
					<TextControl
						label={ __( 'Topics', 'bbp-core' ) }
						type="number"
						value={ ppp }
						onChange={ ( value ) =>
							setAttributes( { ppp: parseInt( value ) } )
						}
						help={ __(
							'Maximum number of topics.',
							'bbp-core'
						) }
					/>
					<SelectControl
						label={ __( 'Order', 'bbp-core' ) }
						value={ order }
						options={ [
							{ label: 'ASC', value: 'ASC' },
							{ label: 'DESC', value: 'DESC' },
						] }
						onChange={ ( value ) =>
							setAttributes( { order: value } )
						}
					/>
					<TextControl
						label={ __( 'Number of Words', 'bbp-core' ) }
						type="number"
						value={ word_length }
						onChange={ ( value ) =>
							setAttributes( {
								word_length: parseInt( value ),
							} )
						}
					/>
					<TextControl
						label={ __( 'Read More Text', 'bbp-core' ) }
						value={ read_more }
						onChange={ ( value ) =>
							setAttributes( { read_more: value } )
						}
					/>
				</PanelBody>

				{ /* -------- Visibility -------- */ }
				<PanelBody
					title={ __( 'Visibility', 'bbp-core' ) }
					initialOpen={ false }
				>
					<div className={ ! isProActive ? 'bbpc-pro-gated' : '' }>
						{ ! isProActive && (
							<span className="bbpc-pro-watermark">PRO</span>
						) }
						<ToggleControl
							label={ __( 'Show Voice Count', 'bbp-core' ) }
							checked={ isProActive ? show_voice : true }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( { show_voice: value } );
								}
							} }
							disabled={ ! isProActive }
						/>
						<ToggleControl
							label={ __( 'Show Reply Count', 'bbp-core' ) }
							checked={ isProActive ? show_replies : true }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( { show_replies: value } );
								}
							} }
							disabled={ ! isProActive }
						/>
						<ToggleControl
							label={ __(
								'Show Freshness / Last Active',
								'bbp-core'
							) }
							checked={ isProActive ? show_freshness : true }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( { show_freshness: value } );
								}
							} }
							disabled={ ! isProActive }
						/>
						<ToggleControl
							label={ __( 'Show Excerpt', 'bbp-core' ) }
							checked={ isProActive ? show_excerpt : true }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( { show_excerpt: value } );
								}
							} }
							disabled={ ! isProActive }
						/>
						<ToggleControl
							label={ __( 'Show "View All" Button', 'bbp-core' ) }
							checked={ isProActive ? show_view_all : true }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( { show_view_all: value } );
								}
							} }
							disabled={ ! isProActive }
						/>
						<ToggleControl
							label={ __(
								'Show Cover Image Badges',
								'bbp-core'
							) }
							checked={ isProActive ? show_cover_badges : true }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( { show_cover_badges: value } );
								}
							} }
							disabled={ ! isProActive }
						/>
					</div>
				</PanelBody>

				{ /* -------- Responsive -------- */ }
				<PanelBody
					title={ __( 'Responsive', 'bbp-core' ) }
					initialOpen={ false }
				>
					<div className={ ! isProActive ? 'bbpc-pro-gated' : '' }>
						{ ! isProActive && (
							<span className="bbpc-pro-watermark">PRO</span>
						) }
						<p className="components-base-control__help">
							{ __(
								'Toggle which columns to hide on mobile devices (< 560px).',
								'bbp-core'
							) }
						</p>
						<ToggleControl
							label={ __(
								'Hide Voice Count on Mobile',
								'bbp-core'
							) }
							checked={ isProActive ? hide_voice_mobile : true }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( { hide_voice_mobile: value } );
								}
							} }
							disabled={ ! isProActive }
						/>
						<ToggleControl
							label={ __(
								'Hide Reply Count on Mobile',
								'bbp-core'
							) }
							checked={ isProActive ? hide_replies_mobile : false }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( {
										hide_replies_mobile: value,
									} );
								}
							} }
							disabled={ ! isProActive }
						/>
						<ToggleControl
							label={ __(
								'Hide User / Freshness on Mobile',
								'bbp-core'
							) }
							checked={ isProActive ? hide_user_mobile : true }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( { hide_user_mobile: value } );
								}
							} }
							disabled={ ! isProActive }
						/>
					</div>
				</PanelBody>
			</InspectorControls>

			{ /* ======== STYLE TAB ======== */ }
			<InspectorControls group="styles">
				{ /* -------- Colors -------- */ }
				<PanelBody
					title={ __( 'Colors', 'bbp-core' ) }
					initialOpen={ false }
				>
					<p style={ { marginBottom: '8px', fontWeight: 500 } }>
						{ __( 'Title Color', 'bbp-core' ) }
					</p>
					<ColorPalette
						value={ title_color }
						onChange={ ( value ) =>
							setAttributes( { title_color: value } )
						}
					/>

					<p style={ { marginBottom: '8px', fontWeight: 500 } }>
						{ __( 'Title Hover Color', 'bbp-core' ) }
					</p>
					<ColorPalette
						value={ title_hover_color }
						onChange={ ( value ) =>
							setAttributes( { title_hover_color: value } )
						}
					/>

					<p style={ { marginBottom: '8px', fontWeight: 500 } }>
						{ __( 'Excerpt Color', 'bbp-core' ) }
					</p>
					<ColorPalette
						value={ excerpt_color }
						onChange={ ( value ) =>
							setAttributes( { excerpt_color: value } )
						}
					/>

					<p style={ { marginBottom: '8px', fontWeight: 500 } }>
						{ __( 'Meta Color', 'bbp-core' ) }
					</p>
					<ColorPalette
						value={ meta_color }
						onChange={ ( value ) =>
							setAttributes( { meta_color: value } )
						}
					/>

					<p style={ { marginBottom: '8px', fontWeight: 500 } }>
						{ __( 'Background Color', 'bbp-core' ) }
					</p>
					<ColorPalette
						value={ bg_color }
						onChange={ ( value ) =>
							setAttributes( { bg_color: value } )
						}
					/>

					<p style={ { marginBottom: '8px', fontWeight: 500 } }>
						{ __( 'Cover Overlay Color', 'bbp-core' ) }
					</p>
					<ColorPalette
						value={ cover_overlay_color }
						onChange={ ( value ) =>
							setAttributes( { cover_overlay_color: value } )
						}
					/>
				</PanelBody>

				{ /* -------- Typography -------- */ }
				<PanelBody
					title={ __( 'Typography', 'bbp-core' ) }
					initialOpen={ false }
				>
					<RangeControl
						label={ __( 'Title Font Size (px)', 'bbp-core' ) }
						value={ title_font_size }
						onChange={ ( value ) =>
							setAttributes( { title_font_size: value } )
						}
						min={ 10 }
						max={ 40 }
						allowReset
					/>
					<RangeControl
						label={ __( 'Meta Font Size (px)', 'bbp-core' ) }
						value={ meta_font_size }
						onChange={ ( value ) =>
							setAttributes( { meta_font_size: value } )
						}
						min={ 10 }
						max={ 24 }
						allowReset
					/>
					<SelectControl
						label={ __( 'Title Font Weight', 'bbp-core' ) }
						value={ title_font_weight }
						options={ [
							{ label: '400 (Normal)', value: '400' },
							{ label: '500 (Medium)', value: '500' },
							{ label: '600 (Semi Bold)', value: '600' },
							{ label: '700 (Bold)', value: '700' },
						] }
						onChange={ ( value ) =>
							setAttributes( { title_font_weight: value } )
						}
					/>
				</PanelBody>

				{ /* -------- Spacing -------- */ }
				<PanelBody
					title={ __( 'Spacing', 'bbp-core' ) }
					initialOpen={ false }
				>
					<RangeControl
						label={ __( 'Row Padding (px)', 'bbp-core' ) }
						value={ row_padding }
						onChange={ ( value ) =>
							setAttributes( { row_padding: value } )
						}
						min={ 4 }
						max={ 40 }
					/>
					<RangeControl
						label={ __( 'Row Gap (px)', 'bbp-core' ) }
						value={ row_gap }
						onChange={ ( value ) =>
							setAttributes( { row_gap: value } )
						}
						min={ 0 }
						max={ 30 }
					/>
					<RangeControl
						label={ __( 'Cover Image Height (px)', 'bbp-core' ) }
						value={ cover_height }
						onChange={ ( value ) =>
							setAttributes( { cover_height: value } )
						}
						min={ 100 }
						max={ 500 }
					/>
					<RangeControl
						label={ __( 'Border Radius (px)', 'bbp-core' ) }
						value={ border_radius }
						onChange={ ( value ) =>
							setAttributes( { border_radius: value } )
						}
						min={ 0 }
						max={ 30 }
					/>
				</PanelBody>
			</InspectorControls>

			{ /* -------- Block Content -------- */ }
			{ ! forum_id ? (
				<Placeholder
					icon={ <Icon icon="admin-post" /> }
					label={ __( 'BBPC Single Forum', 'bbp-core' ) }
					instructions={ __(
						'Select a forum from the block settings panel to display it here.',
						'bbp-core'
					) }
				>
					<SelectControl
						value={ forum_id }
						options={ forumOptions }
						onChange={ ( value ) =>
							setAttributes( { forum_id: value } )
						}
					/>
				</Placeholder>
			) : (
				<ServerSideRender
					block="bbp-core/single-forum"
					attributes={ attributes }
					LoadingResponsePlaceholder={ SkeletonLoader }
				/>
			) }
		</div>
	);
}
